import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import { useRegionLabels } from 'app/shared/hooks/useRegionLabels';
import { RegionDescriptionType } from 'app/shared/model/enumerations/region-description-type.model';
import {Button, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row} from 'reactstrap';
import {AvForm, AvGroup} from 'availity-reactstrap-validation';
import {IDriver} from 'app/shared/model/driver.model';
import {IAmbulance} from 'app/shared/model/ambulance.model';
import {getDriversCompact} from 'app/entities/driver/driver.reducer';
import {getAmbulancesCompact} from 'app/entities/ambulance/ambulance.reducer';
import {IJourney} from 'app/shared/model/journey.model';
import VillageAvTypeahead from 'app/shared/layout/menus/village-av-typeahead';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {hasAnyPermission} from "app/shared/auth/permissions";
import {JourneyDriverType} from "app/entities/dispatcher-dashboard/components/journey-info";


interface SearchDriverModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (driverType: JourneyDriverType, selectedDriver: any) => void;
  journey: IJourney;
  driverType?: JourneyDriverType;
}

const SearchDriverModalComponent = (props: SearchDriverModalProps) => {
  const {isOpen, toggle, onSave, journey, driverType} = props;
  const {
    drivers,
    ambulances,
    driversLoading,
    ambulancesLoading,
  } = props;

  // State
  const [selectedDriver, setSelectedDriver] = useState<IDriver | IAmbulance | null>(null);
  const [clearValue, setClearValue] = useState(false);

  const patientCountryId = journey?.patientCase?.patient?.countryId;

  // Load drivers and ambulances on component mount
  useEffect(() => {
    if (isOpen && patientCountryId) {
      if ((drivers.length === 0 || drivers[0]?.countryId !== patientCountryId) && driverType === JourneyDriverType.COMMUNITY) {
        // Get compact drivers for the country
        props.getDrivers(patientCountryId);
      }
      if ((ambulances.length === 0 || ambulances[0]?.countryId !== patientCountryId) && driverType === JourneyDriverType.AMBULANCE) {
        // Get compact ambulances for the country
        props.getAmbulances(patientCountryId);
      }
    }
  }, [isOpen, patientCountryId]);

  const driverOptions = drivers.map(driver => ({
    id: driver.id,
    short: `${driver.firstName} ${driver.lastName}`,
    full: `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`,
    data: driver
  }));

  const ambulanceOptions = ambulances.map(ambulance => ({
    id: ambulance.id,
    short: `${ambulance.driverFirstName} ${ambulance.driverLastName}`,
    full: `${ambulance.driverFirstName} ${ambulance.driverLastName} - ${ambulance.phoneNumber}`,
    data: ambulance
  }));

  // Clear on modal close
  useEffect(() => {
    if (!isOpen) {
      setSelectedDriver(null);
      setClearValue(prev => !prev);
    }
  }, [isOpen]);

  // Handle driver selection
  const handleDriverSelection = (driverId: string) => {
    const driver = driverType === JourneyDriverType.COMMUNITY ? drivers.find(d => d.id === driverId) : ambulances.find(a => a.id === driverId);
    setSelectedDriver(driver);
  };

  // Handle save button click
  const handleSave = () => {
    if (selectedDriver) {
      onSave(driverType, selectedDriver);
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Select a Driver
      </ModalHeader>
      <ModalBody>
        {/* Journey information */}
        <div className="alert alert-info mb-3">
          <p className="mb-0"><strong>Patient is in need of transport from</strong></p>
          <p
            className="mb-0">From: {journey.startVillage?.name || journey.startHealthcareFacility?.name || 'Not specified'}</p>
          <p
            className="mb-0">To: {journey.endVillage?.name || journey.endHealthcareFacility?.name || 'Not specified'}</p>
          <p className="mb-1">Are you able to pick up this patient?</p>
        </div>

        {/* Search bar */}
        <Row className="mb-3">
          <Col md={12}>
            <AvForm>
              <AvGroup>
                <Label htmlFor="driverSearch">
                  {driverType === JourneyDriverType.COMMUNITY ? 'Search for Driver' : 'Search for Ambulance Driver'}
                </Label>
                <VillageAvTypeahead
                  labelKey="full"
                  valueKey="id"
                  data={driverType === JourneyDriverType.COMMUNITY ? driverOptions : ambulanceOptions}
                  placeholder={`Search for ${driverType === JourneyDriverType.COMMUNITY ? 'driver' : 'ambulance'}...`}
                  id="driverSearch"
                  clearValue={clearValue}
                  onChange={handleDriverSelection}
                  loading={driverType === JourneyDriverType.COMMUNITY ? driversLoading : ambulancesLoading}
                  input={{
                    id: 'driverSearch',
                    name: 'driverId',
                  }}
                />
              </AvGroup>
            </AvForm>
          </Col>
        </Row>

        {/* Loading indicator */}
        {(driversLoading || ambulancesLoading) && (
          <div className="text-center mb-3">
            <FontAwesomeIcon icon="spinner" spin className="mr-2"/>
            Loading {driverType === JourneyDriverType.COMMUNITY ? 'drivers' : 'ambulances'}...
          </div>
        )}
      </ModalBody>

      <ModalFooter className="d-block">
        {/* Cancel and Save Buttons */}
        <Row>
          <Col md="6" className="text-left">
            <Button color="secondary" onClick={toggle}>
              <FontAwesomeIcon icon="times"/> Cancel
            </Button>
          </Col>
          <Col md="6" className="text-right">
            <Button
              color="primary"
              onClick={handleSave}
              disabled={!selectedDriver}
            >
              <FontAwesomeIcon icon="check"/> Save
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  drivers: storeState.driver.compactEntities,
  ambulances: storeState.ambulance.compactEntities,
  driversLoading: storeState.driver.compactLoading,
  ambulancesLoading: storeState.ambulance.compactLoading,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
});

const mapDispatchToProps = {
  getDrivers: getDriversCompact,
  getAmbulances: getAmbulancesCompact,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const SearchDriverModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchDriverModalComponent);

export default SearchDriverModal;
